import { supabase } from '../lib/supabase';

// ─── Tenant Guard ────────────────────────────────────────────────────────────
// When an admin is impersonating an account, sessionStorage holds the target
// user's ID. We MUST use that ID — not the real auth session — so every query
// is scoped to the impersonated account's data, not the admin's data.
const tenantGuard = async () => {
  // Check for active impersonation session first
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) {
        return parsed?.targetId;
      }
    } catch (e) {
      // Malformed sessionStorage entry — fall through to real auth
    }
  }

  // No impersonation active — use the real authenticated user
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user?.id;
};

// ─── Case Helpers ─────────────────────────────────────────────────────────────
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, l => `_${l?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const cls = error?.code?.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error?.message) {
    return [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ]?.some(p => p?.test(error?.message));
  }
  return false;
};

// ─── Service ──────────────────────────────────────────────────────────────────
export const foldersService = {

  // LIST — only the target user's folders (impersonated or real)
  async getFolders() {
    const userId = await tenantGuard();
    const { data, error } = await supabase?.from('folders')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: true });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getFolders error:', error?.message);
      return [];
    }
    return toCamelCase(data || []);
  },

  // CREATE — user_id always stamped from tenantGuard (impersonated or real)
  async createFolder(name) {
    const userId = await tenantGuard();
    const now = new Date()?.toISOString();

    const { data, error } = await supabase?.from('folders')?.insert({ user_id: userId, name, created_at: now, updated_at: now })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('createFolder error:', error?.message);
      return null;
    }
    return toCamelCase(data);
  },

  // RENAME — scoped to target user; cannot rename another tenant's folder
  async renameFolder(folderId, newName) {
    const userId = await tenantGuard();
    const { data, error } = await supabase?.from('folders')?.update({ name: newName, updated_at: new Date()?.toISOString() })?.eq('id', folderId)?.eq('user_id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('renameFolder error:', error?.message);
      return null;
    }
    return toCamelCase(data);
  },

  // DELETE — scoped to target user; moves owned forms out first
  async deleteFolder(folderId) {
    const userId = await tenantGuard();

    // Move only THIS user's forms out of the folder
    await supabase?.from('forms')?.update({ folder_id: null })?.eq('folder_id', folderId)?.eq('user_id', userId);

    const { error } = await supabase?.from('folders')?.delete()?.eq('id', folderId)?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('deleteFolder error:', error?.message);
      return false;
    }
    return true;
  },

  // MOVE FORMS — only moves forms owned by the target user
  async moveFormsToFolder(formIds, folderId) {
    const userId = await tenantGuard();
    const { error } = await supabase?.from('forms')?.update({ folder_id: folderId })?.in('id', formIds)?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('moveFormsToFolder error:', error?.message);
      return false;
    }
    return true;
  },

  // REALTIME — filtered to target user at the channel level
  subscribeToFolders(userId, callback) {
    const channel = supabase?.channel(`folders_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'folders', filter: `user_id=eq.${userId}` },
        (payload) => callback(toCamelCase(payload))
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};